import { apiClient, apiUrl } from '@/lib/api'
import type {
  DealDocument,
  DocumentStatus,
  DocumentTemplate,
  Registration,
  RegistrationItem,
  RegistrationItemType,
  RegistrationItemStatus,
  RegistrationStatus,
} from '@/types/documents'

// --- Templates ---

export const fetchDocumentTemplates = async (): Promise<DocumentTemplate[]> =>
  apiClient<DocumentTemplate[]>('/documents/templates')

export type DocumentTemplateWriteBody = {
  key: string
  name: string
  category: string
  body: string
  required_stage?: string
}

export const createDocumentTemplate = async (body: DocumentTemplateWriteBody): Promise<DocumentTemplate> =>
  apiClient<DocumentTemplate>('/documents/templates', { method: 'POST', body })

export const updateDocumentTemplate = async (
  id: string,
  body: DocumentTemplateWriteBody,
): Promise<DocumentTemplate> =>
  apiClient<DocumentTemplate>(`/documents/templates/${id}`, { method: 'PUT', body })

export const deleteDocumentTemplate = async (id: string): Promise<void> =>
  apiClient<void>(`/documents/templates/${id}`, { method: 'DELETE' })

// --- Deal documents ---

export const fetchDealDocuments = async (dealId: string): Promise<DealDocument[]> =>
  apiClient<DealDocument[]>(`/documents/deals/${dealId}/documents`)

export const generateDocument = async (dealId: string, templateId: string): Promise<DealDocument> =>
  apiClient<DealDocument>(`/documents/deals/${dealId}/documents`, {
    method: 'POST',
    body: { template_id: templateId },
  })

export type DocumentPatchBody = {
  status?: DocumentStatus
  notes?: string
}

export const patchDocument = async (id: string, body: DocumentPatchBody): Promise<DealDocument> =>
  apiClient<DealDocument>(`/documents/${id}`, { method: 'PATCH', body })

export const uploadDocumentFile = async (id: string, file: File): Promise<DealDocument> => {
  const form = new FormData()
  form.append('file', file)
  return apiClient<DealDocument>(`/documents/${id}/file`, { method: 'POST', body: form })
}

export const deleteDocument = async (id: string): Promise<void> =>
  apiClient<void>(`/documents/${id}`, { method: 'DELETE' })

// URL of the rendered HTML document — opened in a new tab for printing /
// save-as-PDF (the browser fetches it with the session cookie).
export const documentRenderUrl = (id: string): string => apiUrl(`/documents/${id}/render`)

// --- Registrations ---

export const fetchRegistrations = async (): Promise<Registration[]> =>
  apiClient<Registration[]>('/registrations')

export const fetchDealRegistration = async (dealId: string): Promise<Registration> =>
  apiClient<Registration>(`/registrations/deals/${dealId}/registration`)

export const createDealRegistration = async (dealId: string): Promise<Registration> =>
  apiClient<Registration>(`/registrations/deals/${dealId}/registration`, { method: 'POST' })

export type RegistrationPatchBody = {
  state?: string
  status?: RegistrationStatus
  temp_tag_expires_at?: string
  clear_temp_tag?: boolean
  lienholder_name?: string
  clear_lienholder?: boolean
  notes?: string
}

export const patchRegistration = async (id: string, body: RegistrationPatchBody): Promise<Registration> =>
  apiClient<Registration>(`/registrations/${id}`, { method: 'PATCH', body })

export const deleteRegistration = async (id: string): Promise<void> =>
  apiClient<void>(`/registrations/${id}`, { method: 'DELETE' })

export type RegistrationItemAddBody = {
  item_type: RegistrationItemType
  label: string
  due_date?: string
}

export const addRegistrationItem = async (
  registrationId: string,
  body: RegistrationItemAddBody,
): Promise<RegistrationItem> =>
  apiClient<RegistrationItem>(`/registrations/${registrationId}/items`, { method: 'POST', body })

export type RegistrationItemPatchBody = {
  label?: string
  status?: RegistrationItemStatus
  due_date?: string
  clear_due_date?: boolean
  notes?: string
}

export const patchRegistrationItem = async (
  registrationId: string,
  itemId: string,
  body: RegistrationItemPatchBody,
): Promise<RegistrationItem> =>
  apiClient<RegistrationItem>(`/registrations/${registrationId}/items/${itemId}`, {
    method: 'PATCH',
    body,
  })

export const deleteRegistrationItem = async (
  registrationId: string,
  itemId: string,
): Promise<void> =>
  apiClient<void>(`/registrations/${registrationId}/items/${itemId}`, { method: 'DELETE' })
